/**
 * Field — shared form-field wrapper: label + control + (optional)
 * error + (optional) helper text.
 *
 * Replaces the local `function Field(...)` block that was redeclared in
 * 10 form modals across IAM / Manage / Manufacturing / Products
 * (purged 2026-05-19). Change the visual rules here and every form
 * across the dashboard picks them up — same single-source pattern as
 * tokens.css for colors.
 *
 * Per SOP 13 + tokens.css:
 *   - label: `text-body-sm` warm-charcoal
 *   - error: `text-body-xs` terracotta — takes precedence over helper
 *   - helper: `text-body-xs` warm-gray — shown only when no error
 *
 * The wrapping `<label>` is intentional so clicking the label focuses
 * the inner control via standard HTML semantics.
 */

import type { ReactNode } from 'react'

interface FieldProps {
  /** Label content. Accepts ReactNode so call sites can pass JSX
   *  (badges, icons, links) alongside the common string case. */
  readonly label: ReactNode
  /** Error text below the control. `null` hides the error slot. */
  readonly error: string | null
  /** Helper text below the control. Shown only when `error` is null. */
  readonly helper?: string
  readonly children: ReactNode
}

export function Field({ label, error, helper, children }: FieldProps): JSX.Element {
  return (
    <label className="block">
      <span className="block text-body-sm font-body text-text-primary mb-1">{label}</span>
      {children}
      {error !== null ? (
        <span className="block text-body-xs font-body text-terra mt-1">{error}</span>
      ) : helper !== undefined ? (
        <span className="block text-body-xs font-body text-text-secondary mt-1">{helper}</span>
      ) : null}
    </label>
  )
}
